import type { CSSProperties } from 'react';
import { type Inline, parseCuratedText } from '../lib/curatedText';

/**
 * Draws curated text — what an officer or admin wrote for the whole unit — TROOP-255. **The only
 * renderer of its kind**: one grammar, one allow-list, and a second renderer is the bug
 * (`CuratedTextSurfaceTests` names the files allowed to use this one).
 *
 * Never for discussion. A message, a reply under an announcement, a thread preview: those are text
 * anybody posts, and they stay literal characters with no links in them.
 *
 * **Every link carries its hostname beside it**, in full and never shortened — "Medical form
 * (scouting.org)". A label is whatever the officer typed; the host is the only part that says where
 * a tap actually lands. It wraps rather than truncating, because an ellipsis is exactly where
 * `council.org.example.com` hides its last word.
 *
 * SPEC-GAP: the design system names no rich-text block. Built from existing tokens only — the
 * body's own type step, `--color-link` as every anchor already wears it, `--space-2` between
 * blocks, and the caption step for the host.
 */
export function CuratedText({
  text,
  className = 't-body',
  style,
}: {
  text: string;
  className?: string;
  /** Colour and outer margin are the caller's — the card and the family row draw it differently. */
  style?: CSSProperties;
}) {
  const blocks = parseCuratedText(text);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', ...style }}>
      {blocks.map((block, b) =>
        block.kind === 'paragraph' ? (
          <p key={b} style={{ margin: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
            <Inlines content={block.content} />
          </p>
        ) : block.ordered ? (
          <ol key={b} start={block.start} style={{ ...listStyle, listStyleType: 'decimal' }}>
            {block.items.map((item, i) => (
              <li key={i}>
                <Inlines content={item} />
              </li>
            ))}
          </ol>
        ) : (
          <ul key={b} style={{ ...listStyle, listStyleType: 'disc' }}>
            {block.items.map((item, i) => (
              <li key={i}>
                <Inlines content={item} />
              </li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}

/**
 * Tailwind's reset takes the markers and the indent away; a list a leader typed wants both.
 * `pre-wrap` here and on a paragraph keeps a run of spaces a leader typed, as the plain body did.
 */
const listStyle: CSSProperties = {
  margin: 0,
  paddingLeft: 'var(--space-5)',
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
};

function Inlines({ content }: { content: Inline[] }) {
  return (
    <>
      {content.map((inline, i) => {
        switch (inline.kind) {
          case 'text':
            return <span key={i}>{inline.text}</span>;
          case 'break':
            return <br key={i} />;
          case 'strong':
            return (
              <strong key={i}>
                <Inlines content={inline.children} />
              </strong>
            );
          case 'em':
            return (
              <em key={i}>
                <Inlines content={inline.children} />
              </em>
            );
          case 'link':
            return (
              <span key={i}>
                <a href={inline.href} target="_blank" rel="noopener noreferrer">
                  {inline.label}
                </a>
                {/* The destination. This is the whole mitigation, and it costs one span. */}
                <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                  {' '}({inline.host})
                </span>
              </span>
            );
        }
      })}
    </>
  );
}
